import Moment from 'moment';
import { extendMoment } from 'moment-range';
import { getAll } from '../../api';
import { search as createSearch } from '@/_store/utils/app/search';
import { stringyObject } from '../utils/helpers';
import { createPaginatedRequest } from '../utils/app/paginatedRequestBuilder';
import {
  callString,
  screenCallString,
  filterParams,
  screenFilterParams,
  sdate,
  edate,
} from '../utils/reconciliation/requestConfig';
import { createRequest } from '../utils/app/requestBuilder';
import { getFilters } from '../utils/app/filter';

const moment = extendMoment(Moment);

// state
const state = {
  overdueChart: {
    data: [
      {
        name: 'Overdue',
        data: [0, 0, 0, 0],
      },
      {
        name: 'Reported',
        data: [0, 0, 0, 0],
      },
    ],
    title: 'Rental Data',
    secondaryTitle: 'Overdue vs Reported',
    labels: [],
  },
  data: {},
  queryParams: {
    page: 1,
    search: '',
  },
  instances: [],
  oneWayInstances: [],
  callStatusChart: {
    data: [0, 0, 0, 0, 0, 0],
    labels: [
      'None',
      '1st attempt',
      '2nd attempt',
      'Escalated',
      'Located',
      'Missing',
    ],
    title: 'Call Status',
  },
  tab: 0,
  export: false,
  dataExport: false,
  exportConfirmation: false,
  lastEvalKey: undefined,
  exportURL: {
    csv: '',
    excel: '',
    loaded: false,
  },
};

// getters
const getters = {
  getOverdueChart(state) {
    return state.overdueChart;
  },
  getData(state) {
    return state.data;
  },
  getInstances(state) {
    return state.instances;
  },
  getOneWayInstances(state) {
    return state.oneWayInstances;
  },
  getQueryParams(state) {
    return state.queryParams;
  },
  getCallStatusChart(state) {
    return state.callStatusChart;
  },
  getTab(state) {
    return state.tab;
  },
  getExport(state) {
    return state.export;
  },
  getDataExport(state) {
    return state.dataExport;
  },
  getExportConfirmation(state) {
    return state.exportConfirmation;
  },
  getExportURL(state) {
    return state.exportURL;
  },
  getLastEvalKey(state) {
    return state.lastEvalKey;
  },
};

// actions
const actions = {
  acquireRentalData: async (
    {
      commit, rootState, rootGetters, dispatch,
    },
    paginate,
  ) => new Promise((resolve, reject) => {
    commit('loading/setLoading', true, { root: true });

    createPaginatedRequest(
      filterParams,
      callString,
      'session',
      paginate,
      sdate,
      edate,
    )
      .then((res) => {
        commit(
          'setLastEvalKey',
          res.data.data.getPaginated.attr.LastEvaluatedKey,
        );
        if (state.tab === 0) {
          dispatch('acquireSessionScreens', {
            data: res.data.data.getPaginated.items,
            filter: getFilters(),
          });
        }
        const allGroups = rootState.account.currentClient.groups;
        const groups = [];
        for (let i = 0; i < allGroups.length; i++) {
          groups.push({
            text: allGroups[i].group_name,
            value: allGroups[i].uuid,
          });
        }
        const tablets = [];
        for (let i = 0; i < res.data.data.getPaginated.items.length; i++) {
          tablets.push(res.data.data.getPaginated.items[i].screen_id);
        }
        const clientId = rootGetters['account/getCurrentClient'].uuid;
        let tripTypeOptions = [];
        switch (clientId) {
          case '4617860c-4690-4a27-835a-1d9d1bfa4624':
            tripTypeOptions = ['QR Code', 'Digital Key'];
            break;
          case '4aa0754b-6e96-4092-bc61-da873d487628':
            tripTypeOptions = ['Bluetooth', 'Premium'];
            break;
          case '66993187-282c-4932-a7c0-153eb0b253a2':
            tripTypeOptions = ['Round Trip', 'One Way'];
            break;
          default:
            tripTypeOptions = ['Round Trip', 'One Way'];
            break;
        }
        commit(
          'filters/setReconciliationReturnFilterOptions',
          {
            confimation: ['Confirmed', 'Not Confirmed'],
            callStatus: [],
            groups,
            rentalStatus: [],
            tripType: tripTypeOptions,
            rentalStatusType: ['Checked In', 'Checked Out'],
            tablets,
          },
          { root: true },
        );
        commit('loading/setLoading', false, { root: true });
        resolve(res);
      })
      .catch((err) => {
        commit('loading/setLoading', false, { root: true });
        reject(err);
      });
  }),
  acquireOneWayData: async ({
    commit, rootState, rootGetters, dispatch,
  }) => new Promise((resolve, reject) => {
    let variables = {};
    const filters = rootState.filters.filter.oneWay;
    // if (filters.confirmed !== "" && filters.confirmed !== undefined) {
    //   variables.confirmed = filters.confirmed //screen
    // }
    // if (filters.callStatus !== "" && filters.callStatus !== undefined) {
    //   variables.call_status = filters.callStatus
    // }
    // if (filters.rentalStatus !== "" && filters.rentalStatus !== undefined) {
    //   variables.session_status = filters.rentalStatus //screen
    // }
    if (filters.searchTablets !== '' && filters.searchTablets !== undefined) {
      variables.screen_id = filters.searchTablets;
    }
    if (filters.search !== '' && filters.search !== undefined) {
      search.uuid = filters.oneWay.search;
    }
    const search = createSearch();
    variables = stringyObject(variables);
    const clientTable = rootGetters['account/getCurrentClient'].table_name;
    getAll(
      `/session?query=${
        encodeURIComponent(
          `{get(type_name: "session", 
            filters: ${variables},
            search: ${search},
            table_name:"${clientTable}", 
            index: "all-index"
          ){
            uuid, 
            area_id, 
            group_id, 
            screen_id,
            ra_number,
            expected_end_at, 
            start_at, 
            trip_type, 
            end_at, 
            rider_email, 
            error, 
            success
          }}`,
        )}`,
    )
      .then((res) => {
        if (state.tab === 1) {
          dispatch('acquireSessionScreens', {
            data: res.data.data.get,
          });
        }
        const tablets = [];
        for (let i = 0; i < res.data.data.get.length; i++) {
          tablets.push(res.data.data.get[i].screen_id);
        }
        commit(
          'filters/setReconciliationOneWayFilterOptions',
          {
            confirmation: ['Confirmed', 'Not Confirmed'],
            callStatus: [],
            tablets,
            contacts: [],
            rentalStatusType: ['Checked In', 'Checked Out'],
          },
          { root: true },
        );
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  }),
  exportReport: async ({ commit, rootGetters }, payload) => new Promise((resolve, reject) => {
    let cols = [];
    if (payload.device_id) {
      cols.push({ id: 'screen_id', title: 'Screen' });
    }
    // if (payload.accessory_name) {
    //   cols.push({ id: "", title: "Accessory Name" })
    // }
    // if (payload.accessory_status) {
    //   cols.push({ id: "", title: "Accessory Status" })
    // }
    if (payload.ra_number) {
      cols.push({ id: 'ra_number', title: 'Rental Agreement #' });
    }
    if (payload.trip_type) {
      cols.push({ id: 'trip_type', title: 'Trip Type' });
    }
    if (payload.rental_start_date) {
      cols.push({ id: 'start_at', title: 'Rental Start Date' });
    }
    if (payload.expected_due_date) {
      cols.push({ id: 'expected_end_at', title: 'Exspected End Date' });
    }
    // if (payload.reconciled_by) {
    //   cols.push({ id: "", title: "Reconciled By" })
    // }
    cols = stringyObject(cols);
    const clientTable = rootGetters['account/getCurrentClient'].table_name;
    getAll(
      `/session?query=${
        encodeURIComponent(
          `{export(
            csv: ${payload.csv}, 
            excel: ${payload.excel}, 
            filters: {type_name: "session"}, 
            cols: ${cols}, 
            type_name: "session", 
            table_name:"${clientTable}", 
            index: "all-index"
          ){
            csv,
            excel,
            success
          }}`,
        )}`,
    )
      .then((res) => {
        commit('setExportURL', {
          csv: res.data.data.export.csv,
          excel: res.data.data.export.excel,
          loaded: true,
        });
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  }),
  acquireSessionScreens: async ({ commit }, payload) => new Promise((reject, resolve) => {
    createRequest(screenFilterParams, screenCallString, 'screen')
      .then((res) => {
        payload.data.map((session) => {
          session.screen = res.data.data.get.find((screen) => screen.uuid === session.screen_id);
        });
        for (let i = 0; i < payload.data.length; i++) {
          if (!payload.data[i].screen) {
            delete payload.data[i];
          }
        }
        payload.data = payload.data.filter((val) => val);
        if (state.tab === 0) {
          commit('setRentalInstances', payload);
        } else {
          commit('setOneWayInstances', payload.data);
        }
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  }),
};

// mutations
const mutations = {
  setOneWayData(state, payload) {
    state.rentalData = payload;
  },
  setRentalData(state, payload) {
    state.oneWayData = payload;
  },
  setRentalInstances(state, payload) {
    const allSessions = [];
    const oneWaySessions = [];
    const newstart = moment(payload.filter.start_date);
    const newend = moment(payload.filter.end_date);
    const range = moment.range(newstart, newend);
    let format = 'YYYY';
    const years = Array.from(range.by('year'));
    let timeValues = years;
    let timeValue = 'y';
    if (years.length <= 2) {
      const months = Array.from(range.by('month'));
      timeValues = months;
      timeValue = 'M';
      format = 'MMMM';
      if (months.length <= 4) {
        const weeks = Array.from(range.by('week'));
        timeValues = weeks;
        timeValue = 'w';
        format = 'MM-DD';
        if (weeks.length <= 2) {
          const days = Array.from(range.by('day'));
          timeValues = days;
          timeValue = 'd';
          format = 'MM-DD';
          if (days.length <= 2) {
            const hours = Array.from(range.by('hour'));
            timeValues = hours;
            timeValue = 'h';
            format = 'HH:mm';
          }
        }
      }
    }
    const allRanges = [];
    timeValues.map((time, index) => {
      if (timeValues[index + 1]) {
        const range = moment.range(time, timeValues[index + 1]);
        allRanges.push(range);
      } else {
        const range = moment.range(time, moment(time).add(1, timeValue));
        allRanges.push(range);
      }
    });
    const chartData = [
      {
        name: 'Overdue',
        data: [],
      },
      {
        name: 'Reported',
        data: [],
      },
    ];
    state.overdueChart.labels = timeValues.map((m) => m.format(format));
    payload.data.map((session) => {
      if (
        (session.trip_type
          && session.trip_type.toUpperCase().replace(' ', '_') === 'ONE_WAY')
        || (session.trip_type
          && session.trip_type.toUpperCase().replace(' ', '_') === 'QR_CODE')
        || (session.trip_type
          && session.trip_type.toUpperCase().replace(' ', '_') === 'BLUE_TOOTH')
      ) {
        oneWaySessions.push(session);
      }
      allSessions.push(session);
    });
    allRanges.map((range, index) => {
      chartData[1].data[index] = 0;
      chartData[0].data[index] = 0;
      allSessions.map((session) => {
        if (range.contains(moment(session.expected_end_at * 1000))) {
          if (session.end_at || session.start_at > session.expected_end_at) {
            chartData[1].data[index] = chartData[1].data[index] + 1;
          } else {
            chartData[0].data[index] = chartData[0].data[index] + 1;
          }
        }
      });
    });
    state.overdueChart.data = chartData;
    state.instances = allSessions;
  },
  setOneWayInstances(state, payload) {
    const oneWaySessions = [];
    payload.map((session) => {
      if (session.end_at) {
        session.rental_status = 'Checked In';
      } else {
        session.rental_status = 'Checked Out';
      }
      if (
        session.trip_type === 'ONE_WAY'
        || session.trip_type === 'one way'
        || session.trip_type === 'QR_CODE'
        || session.trip_type === 'qr code'
        || session.trip_type === 'bluetooth'
      ) {
        oneWaySessions.push(session);
      }
    });
    state.callStatusChart.data = [0, 0, 0, 0, 0, 0];
    oneWaySessions.map((session) => {
      if (session.call_status) {
        switch (session.call_status) {
          case 'None':
            state.callStatusChart.data[0]++;
            break;
          case '1st attempt':
            state.callStatusChart.data[1]++;
            break;
          case '2nd attempt':
            state.callStatusChart.data[2]++;
            break;
          case 'Escalated':
            state.callStatusChart.data[3]++;
            break;
          case 'Located':
            state.callStatusChart.data[4]++;
            break;
          case 'Missing':
            state.callStatusChart.data[5]++;
            break;
        }
      } else if (!session.call_status) {
        state.callStatusChart.data[0]++;
      }
    });
    /* oneWaySessions.sort((a, b) =>
      b.expected_end_at.toString().localeCompare(a.expected_end_at.toString())
    ); */
    state.oneWayInstances = oneWaySessions;
  },
  setQueryParams(state, payload) {
    state.queryParams = payload;
  },
  setTab(state, payload) {
    state.tab = payload;
  },
  setExport(state, payload) {
    state.export = payload;
  },
  setDataExport(state, payload) {
    state.dataExport = payload;
  },
  setExportConfirmation(state, payload) {
    state.exportConfirmation = payload;
  },
  setExportURL(state, payload) {
    state.exportURL = payload;
  },
  setLastEvalKey(state, payload) {
    state.lastEvalKey = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
